import React, { FC, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
  Warehouse as WarehouseIcon, MapPin, Printer, Layers, BookOpen, Trash2, Plus,
  RefreshCw, Search, Move, Boxes, Package, Info,
} from 'lucide-react';
import ContextStore from '../hooks/ContextStore';
import { dispatch } from '../dispatch/dispatch';
import { actions } from '../dispatch';
import './Warehouse.page.css';

// ─── Types ───────────────────────────────────────────────────────────────────
type Abc = 'A' | 'B' | 'C' | '-';
type Size = 'S' | 'M' | 'L' | 'XL' | 'XXL';
type Zone = 'FAST' | 'MEDIUM' | 'SLOW' | 'BULK' | 'RETURNS' | 'PACKING';

interface SlotRow {
  groupId: string; name: string; imageUrl: string | null; skus: string[]; skuCount: number;
  stock: number; damaged: number; units90: number; units30: number; orders90: number; lastPick: string | null;
  abc: Abc; sizeClass: Size; sizeGuessed: boolean; sizeSource: 'set' | 'dims' | 'guess'; cartonCm: { l: number | null; w: number | null; h: number | null } | null; weightKg: number | null; packingNote: string;
  location: string; locationKnown: boolean; zone: Zone | null; overflowLocations: string[];
  suggestedZone: Zone | null; locationUpdatedAt: string | null; needsSlot: boolean; walk: any[];
}
interface Slotting {
  generatedAt: string;
  totals: { groups: number; needsSlot: number; placed: number; unplaced: number; unknownCode: number; deadStock: number; deadUnits: number; byClass: Record<string, number>; bySize: Record<string, number> };
  rows: SlotRow[];
}
// Stock is shown as a level, never as a count (user 2026-09-23): the map says whether a slot holds
// the product, the exact quantity stays in Inventory Control.
const StockLevel = ({ stock }: { stock: number | null | undefined }) => {
  if (stock == null) return <span className="wh__chip wh__abc-C">ما معروفش</span>;
  if (stock <= 0) return <span className="wh__chip wh__abc--">خاوي</span>;
  if (stock <= 3) return <span className="wh__chip wh__zone-BULK">قليل</span>;
  return <span className="wh__chip wh__abc-A">كاين</span>;
};

interface MapProduct { groupId: string; name: string; imageUrl: string | null; sizeClass: Size; stock: number; kind: 'primary' | 'overflow' }
interface Loc {
  _id: string; code: string; zone: Zone; aisle: string; bay: number; level: number; type: string;
  fits: Size[]; capacity: number; notes: string; active: boolean; walkOrder: number | null; flat?: boolean; sequential?: boolean;
  products: MapProduct[]; used: number;
}
interface MapResp { locations: Loc[]; orphans: { code: string; products: MapProduct[] }[]; zones: Zone[]; sizeClasses: Size[] }
interface PlanItem { groupId: string; name: string; imageUrl: string | null; abc: Abc; sizeClass: Size; units90: number; stock: number; from: string; to: string; zone: Zone }
interface MoveItem {
  groupId: string; name: string; imageUrl: string | null; sizeClass: Size; abc90: Abc; abc30: Abc; units90: number; units30: number; stock: number;
  from: string; fromZone: Zone; toZone: Zone; to: string | null; direction: 'forward' | 'back'; reason: string; impact: number; snoozedUntil: string | null;
}
interface MovesResp { generatedAt: string; counts: { forward: number; back: number; snoozed: number; noSlot: number }; suggestions: MoveItem[] }
interface PlanResp { plan: PlanItem[]; unplaced: { groupId: string; name: string; sizeClass: Size; suggestedZone: Zone | null }[]; capacityLeft: number }

const ZONES: Zone[] = ['FAST', 'MEDIUM', 'SLOW', 'BULK', 'RETURNS', 'PACKING'];
const ZONE_LABEL: Record<Zone, string> = {
  FAST: 'سريع (A)', MEDIUM: 'متوسط (B)', SLOW: 'بطيء / واقف (C)', BULK: 'الحيط — الأرض حدا الحيط (XL, XXL)', RETURNS: 'المرجوعات (معزولين)', PACKING: 'بلاصة التغليف',
};
const SIZES: Size[] = ['S', 'M', 'L', 'XL', 'XXL'];
const SIZE_HINT: Record<Size, string> = {
  S: 'حتى 30 سم و 10 لتر — صندوق فوق الرف', M: 'حتى 60 سم و 60 لتر — كرطونة فوق الرف', L: 'كرطونة كبيرة ولكن كتدخل فطبقة وحدة — نتا اللي كتختارها، عمرها ما كتتخمّن',
  XL: 'كبر من M، حتى 160 سم و 25 كيلو — قسم فالحيط', XXL: 'كثر من 160 سم، 300 لتر ولا 25 كيلو — الحيط، جوج د الناس',
};
const dimsText = (c: { l: number | null; w: number | null; h: number | null } | null) => (c && (c.l || c.w || c.h) ? [c.l, c.w, c.h].map((v) => v ?? '').join('x') : '');
const parseDims = (s: string) => {
  const m = s.replace(/,/g, '.').match(/(\d+(?:\.\d+)?)\s*[x×*]\s*(\d+(?:\.\d+)?)(?:\s*[x×*]\s*(\d+(?:\.\d+)?))?/i);
  if (!m) return s.trim() ? undefined : { l: null, w: null, h: null };
  return { l: Number(m[1]), w: Number(m[2]), h: m[3] ? Number(m[3]) : null };
};
const isErr = (r: any) => !r || (typeof r === 'object' && 'error' in r);
const errMsg = (r: any) => r?.error?.message || 'الطلب ما دازش';
const openBlob = (r: any) => {
  if (isErr(r) || !r.blob) return false;
  const url = URL.createObjectURL(r.blob);
  window.open(url, '_blank');
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
  return true;
};
const ago = (d: string | null) => {
  if (!d) return '';
  const days = Math.floor((Date.now() - new Date(d).getTime()) / 864e5);
  return days === 0 ? 'اليوم' : days === 1 ? 'البارح' : `قبل ${days} يوم`;
};

// ─── Page ────────────────────────────────────────────────────────────────────
type Tab = 'noplace' | 'slotting' | 'map' | 'moves' | 'plan' | 'guide';

const WarehousePage: FC = () => {
  const { contextStore } = useContext(ContextStore);
  const token = contextStore.token;

  const [tab, setTab] = useState<Tab>('noplace');   // the warehouse boy lands on what still needs a place (user 2026-10-25)
  const [slotting, setSlotting] = useState<Slotting | null>(null);
  const [map, setMap] = useState<MapResp | null>(null);
  const [moves, setMoves] = useState<MovesResp | null>(null);
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState<{ kind: 'ok' | 'err' | 'info'; text: string } | null>(null);

  const flash = (kind: 'ok' | 'err' | 'info', text: string) => {
    setMsg({ kind, text });
    setTimeout(() => setMsg((m) => (m && m.text === text ? null : m)), 6000);
  };

  const load = useCallback(async () => {
    setLoading(true);
    const [s, m, mv] = await Promise.all([
      dispatch(actions.getWarehouseSlotting, {}, token),
      dispatch(actions.getWarehouseMap, {}, token),
      dispatch(actions.getWarehouseMoves, {}, token),
    ]);
    setLoading(false);
    if (isErr(s)) return flash('err', errMsg(s));
    if (isErr(m)) return flash('err', errMsg(m));
    setSlotting(s);
    setMap(m);
    if (!isErr(mv)) setMoves(mv);
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const codes = useMemo(() => (map?.locations || []).map((l) => l.code), [map]);
  const t = slotting?.totals;

  // Any product picture on the page (table thumbs, slot thumbs) opens big on click
  const [lightbox, setLightbox] = useState<{ src: string; name: string } | null>(null);
  const onThumbClick = (e: React.MouseEvent) => {
    const t = e.target as HTMLElement;
    if (t.tagName !== 'IMG' || !(t.classList.contains('wh__thumb') || t.closest('.wh__cell-thumbs'))) return;
    e.stopPropagation(); e.preventDefault();
    const img = t as HTMLImageElement;
    const cell = img.closest('td'); const name = img.getAttribute('title') || img.getAttribute('alt') || cell?.nextElementSibling?.textContent?.trim().split('\n')[0] || '';
    setLightbox({ src: img.src, name: name.slice(0, 120) });
  };
  useEffect(() => {
    if (!lightbox) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setLightbox(null); };
    window.addEventListener('keydown', onKey); return () => window.removeEventListener('keydown', onKey);
  }, [lightbox]);
  return (
    <div className="wh" dir="rtl" onClickCapture={onThumbClick}>
      {lightbox && (
        <div className="wh__lightbox" onClick={() => setLightbox(null)}>
          <img src={lightbox.src} alt="" onClick={(e) => e.stopPropagation()} />
          {lightbox.name && <div className="wh__lightbox-name">{lightbox.name}</div>}
          <button className="wh__lightbox-close" onClick={() => setLightbox(null)} aria-label="سد">✕</button>
        </div>
      )}
      <div className="wh__head">
        <div className="wh__title-block">
          <div className="wh__title-icon"><WarehouseIcon size={18} /></div>
          <div>
            <div className="wh__title">خريطة المخزن</div>
            <div className="wh__sub">عطي لكل منتج كود بلاصة واحد باش أي واحد يلقاه ف ثواني</div>
          </div>
        </div>
        <button className="wh__btn" onClick={load} disabled={loading}><RefreshCw size={14} className={loading ? 'animate-spin' : ''} /> تحديث</button>
      </div>

      <div className="wh__body">
        {msg && <div className={`wh__msg ${msg.kind === 'err' ? 'wh__msg--err' : msg.kind === 'ok' ? 'wh__msg--ok' : ''}`}>{msg.text}</div>}

        {t && (
          <div className="wh__kpis">
            <div className="wh__kpi"><div className="wh__kpi-label">منتجات خاصها بلاصة</div><div className="wh__kpi-value">{t.needsSlot}</div><div className="wh__kpi-sub">فيها السلعة ولا تباعت ف 30 يوم اللخرين</div></div>
            <div className={`wh__kpi ${t.placed === t.needsSlot ? 'wh__kpi--good' : ''}`}><div className="wh__kpi-label">عندها بلاصة</div><div className="wh__kpi-value">{t.placed}</div><div className="wh__kpi-sub">{t.needsSlot ? Math.round((t.placed / t.needsSlot) * 100) : 0}% عندهم كود البلاصة</div></div>
            <div className={`wh__kpi ${t.unplaced ? 'wh__kpi--warn' : 'wh__kpi--good'}`}><div className="wh__kpi-label">بلا بلاصة</div><div className="wh__kpi-value">{t.unplaced}</div><div className="wh__kpi-sub">A {t.byClass.A} · B {t.byClass.B} · C {t.byClass.C} · بلا بيع {t.byClass['-']}</div></div>
            <div className={`wh__kpi ${t.deadStock ? 'wh__kpi--bad' : ''}`}><div className="wh__kpi-label">سلعة واقفة</div><div className="wh__kpi-value">{t.deadStock}</div><div className="wh__kpi-sub">فيها السلعة، حتى خرجة ف 90 يوم</div></div>
            <div className={`wh__kpi ${moves && moves.counts.forward + moves.counts.back > 0 ? 'wh__kpi--warn' : 'wh__kpi--good'}`} style={{ cursor: 'pointer' }} onClick={() => setTab('moves')}><div className="wh__kpi-label">تبديلات مقترحة</div><div className="wh__kpi-value">{moves ? moves.counts.forward + moves.counts.back : '—'}</div><div className="wh__kpi-sub">{moves ? `${moves.counts.forward} لقدّام · ${moves.counts.back} لور` : 'البيع تبدّل ← البلاصة خاصها تتبدّل'}</div></div>
            {(() => {
              const locs = map?.locations || [];
              const total = locs.length;
              const empty = locs.filter((l) => !l.used).length;
              const full = locs.filter((l) => l.used >= l.capacity).length;
              const placesTotal = locs.reduce((s, l) => s + (l.capacity || 0), 0);
              const placesLeft = locs.reduce((s, l) => s + Math.max(0, (l.capacity || 0) - (l.used || 0)), 0);
              return (
                <>
                  <div className="wh__kpi" style={{ cursor: 'pointer' }} onClick={() => setTab('map')}><div className="wh__kpi-label">البلايص</div><div className="wh__kpi-value">{total}</div><div className="wh__kpi-sub">{total - empty} عامرين · {empty} خاويين · {full} مليانين</div></div>
                  <div className={`wh__kpi ${placesTotal && placesLeft / placesTotal < 0.1 ? 'wh__kpi--warn' : ''}`} style={{ cursor: 'pointer' }} onClick={() => setTab('map')}><div className="wh__kpi-label">المكان الباقي</div><div className="wh__kpi-value">{placesLeft}</div><div className="wh__kpi-sub">من {placesTotal} مكان ({placesTotal ? Math.round((placesLeft / placesTotal) * 100) : 0}% فارغ) · مكان واحد = منتج واحد فبلاصة</div></div>
                </>
              );
            })()}
          </div>
        )}

        <div className="wh__tabs">
          <button className={`wh__tab ${tab === 'noplace' ? 'wh__tab--active' : ''}`} onClick={() => setTab('noplace')}>📍 بلا بلاصة{slotting ? ` (${slotting.rows.filter((r) => r.stock > 0 && !r.location).length})` : ''}</button>
        <button className={`wh__tab ${tab === 'slotting' ? 'wh__tab--active' : ''}`} onClick={() => setTab('slotting')}><Boxes size={14} /> المنتجات والبلايص</button>
          <button className={`wh__tab ${tab === 'map' ? 'wh__tab--active' : ''}`} onClick={() => setTab('map')}><Layers size={14} /> خريطة البلايص</button>
          <button className={`wh__tab ${tab === 'moves' ? 'wh__tab--active' : ''}`} onClick={() => setTab('moves')}><RefreshCw size={14} /> تبديلات مقترحة{moves && moves.counts.forward + moves.counts.back > 0 ? ` (${moves.counts.forward + moves.counts.back})` : ''}</button>
          <button className={`wh__tab ${tab === 'plan' ? 'wh__tab--active' : ''}`} onClick={() => setTab('plan')}><Move size={14} /> خطة أوتوماتيكية</button>
          <button className={`wh__tab ${tab === 'guide' ? 'wh__tab--active' : ''}`} onClick={() => setTab('guide')}><BookOpen size={14} /> القواعد والشرح</button>
        </div>

        {tab === 'noplace' && slotting && <NoPlaceTab rows={slotting.rows} codes={codes} token={token} flash={flash} reload={load} />}
        {tab === 'slotting' && slotting && <SlottingTab rows={slotting.rows} codes={codes} token={token} flash={flash} reload={load} />}
        {tab === 'map' && map && <MapTab map={map} token={token} flash={flash} reload={load} />}
        {tab === 'moves' && <MovesTab moves={moves} token={token} flash={flash} reload={load} />}
        {tab === 'plan' && <PlanTab token={token} flash={flash} reload={load} hasLocations={(map?.locations.length || 0) > 0} />}
        {tab === 'guide' && <GuideTab />}
      </div>
    </div>
  );
};

// ─── 📍 No place tab (user 2026-10-25) ───────────────────────────────────────
// Every product WITH stock but WITHOUT a location, for the warehouse boy: he finds it, sees how many pieces there are
// (read-only — he cannot change the stock here) and types the location. Saving = the same assign as the map.
const NoPlaceTab: FC<{ rows: SlotRow[]; codes: string[]; token: string; flash: (k: 'ok' | 'err', m: string) => void; reload: () => void }> = ({ rows, codes, token, flash, reload }) => {
  const [q, setQ] = useState('');
  const [val, setVal] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState<string | null>(null);
  const [done, setDone] = useState<Record<string, string>>({});
  const list = useMemo(() => rows.filter((r) => r.stock > 0 && !r.location && !done[r.groupId])
    .filter((r) => !q.trim() || r.name.toLowerCase().includes(q.trim().toLowerCase()))
    .sort((a, b) => b.stock - a.stock), [rows, q, done]);
  const save = async (r: SlotRow) => {
    const loc = (val[r.groupId] || '').trim().toUpperCase();
    if (!loc) { flash('err', 'كتب البلاصة أولا'); return; }
    setBusy(r.groupId);
    const resp = await dispatch(actions.assignWarehouseSlot, { location: loc }, token, { groupId: r.groupId });
    setBusy(null);
    if (isErr(resp)) { flash('err', `${r.name}: ${errMsg(resp)}`); return; }
    setDone((d) => ({ ...d, [r.groupId]: resp.location || loc }));
    flash('ok', `✅ ${r.name} → ${resp.location || loc}`);
  };
  return (
    <div dir="rtl" style={{ padding: '8px 0' }}>
      <p style={{ fontSize: 15, fontWeight: 800, margin: '4px 0 2px' }}>📍 منتجات عندنا فيها السلعة ولكن ما عندهاش بلاصة ({list.length})</p>
      <p style={{ fontSize: 13, color: '#64748b', margin: '0 0 10px' }}>قلّب على المنتج فالمخزن، شوف شحال كاين، وكتب البلاصة ديالو (مثلا A5). العدد غير للمعلومة، ما كيتبدلش من هنا.</p>
      <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="قلّب بالسمية…" style={{ width: '100%', padding: '10px 12px', borderRadius: 10, border: '1px solid #cbd5e1', fontSize: 15, marginBottom: 10 }} />
      <datalist id="noplace-codes">{codes.map((c) => <option key={c} value={c} />)}</datalist>
      {list.length === 0 && <p style={{ textAlign: 'center', color: '#047857', fontWeight: 800, padding: 20 }}>✅ كل المنتجات اللي فيها السلعة عندها بلاصة</p>}
      <div style={{ display: 'grid', gap: 8 }}>
        {list.map((r) => (
          <div key={r.groupId} style={{ display: 'flex', gap: 10, alignItems: 'center', background: '#fff', border: '1px solid #e2e8f0', borderRadius: 14, padding: 10, flexWrap: 'wrap' }}>
            {r.imageUrl ? <img src={r.imageUrl} alt="" style={{ width: 64, height: 64, objectFit: 'contain', borderRadius: 10, background: '#f8fafc' }} /> : <div style={{ width: 64, height: 64, borderRadius: 10, background: '#f1f5f9' }} />}
            <div style={{ flex: '1 1 160px', minWidth: 0 }}>
              <div dir="ltr" style={{ fontWeight: 800, fontSize: 14, textAlign: 'right', overflowWrap: 'anywhere' }}>{r.name}</div>
              <div style={{ marginTop: 4, display: 'inline-block', background: '#f1f5f9', borderRadius: 999, padding: '2px 10px', fontSize: 13, fontWeight: 800 }}>📦 {r.stock} قطعة فالمخزون</div>
            </div>
            <div style={{ display: 'flex', gap: 6, flex: '1 1 200px' }}>
              <input dir="ltr" list="noplace-codes" value={val[r.groupId] || ''} onChange={(e) => setVal((v) => ({ ...v, [r.groupId]: e.target.value }))} onKeyDown={(e) => { if (e.key === 'Enter') save(r); }}
                placeholder="البلاصة (مثلا A5)" style={{ flex: 1, minWidth: 0, padding: '10px 12px', borderRadius: 10, border: '2px solid #cbd5e1', fontSize: 18, fontWeight: 800, textTransform: 'uppercase' }} />
              <button disabled={busy === r.groupId} onClick={() => save(r)} style={{ background: '#047857', color: '#fff', border: 'none', borderRadius: 10, padding: '0 16px', fontSize: 15, fontWeight: 800, cursor: 'pointer', opacity: busy === r.groupId ? 0.6 : 1 }}>{busy === r.groupId ? '…' : '💾 حفظ'}</button>
            </div>
          </div>
        ))}
      </div>
      {Object.keys(done).length > 0 && <button onClick={() => { setDone({}); reload(); }} style={{ marginTop: 12, padding: '8px 14px', borderRadius: 10, border: '1px solid #cbd5e1', background: '#fff', fontWeight: 700 }}>🔄 تحديث ({Object.keys(done).length} تحطّو)</button>}
    </div>
  );
};

// ─── Slotting tab ────────────────────────────────────────────────────────────
// Read-only table; every change goes through an explicit editor (tap the size
// badge or the ✎ button) with Save / Cancel, so nothing changes by accident on
// a phone. The slot code can also be typed inline, but it is only sent on
// Enter or with the row's Save button — never on a stray tap.
const SlottingTab: FC<{ rows: SlotRow[]; codes: string[]; token: string; flash: (k: 'ok' | 'err' | 'info', t: string) => void; reload: () => void }> = ({ rows, codes, token, flash, reload }) => {
  const [q, setQ] = useState('');
  const [abc, setAbc] = useState<'' | Abc>('');
  const [size, setSize] = useState<'' | Size>('');
  const [status, setStatus] = useState<'unplaced' | 'placed' | 'all'>('all');
  const [onlyNeeded, setOnlyNeeded] = useState(false);
  const [sel, setSel] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);
  const [draftSlot, setDraftSlot] = useState<Record<string, string>>({});
  const [state, setState] = useState<Record<string, 'saving' | 'saved' | 'error' | undefined>>({});
  const [editing, setEditing] = useState<SlotRow | null>(null);

  useEffect(() => { setDraftSlot({}); }, [rows]);

  const base = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return rows
      .filter((r) => !onlyNeeded || r.needsSlot)
      .filter((r) => !abc || r.abc === abc)
      .filter((r) => !size || r.sizeClass === size)
      .filter((r) => !needle || r.name.toLowerCase().includes(needle) || r.skus.some((x) => x.toLowerCase().includes(needle)) || r.location.toLowerCase().includes(needle))
      .sort((a, b) => b.units90 - a.units90 || b.stock - a.stock);
  }, [rows, q, abc, size, onlyNeeded]);

  // placed / unplaced counts under the current filters (everything except the status filter itself)
  const placedCount = useMemo(() => base.filter((r) => !!r.location).length, [base]);
  const unplacedCount = base.length - placedCount;
  const view = useMemo(
    () => base.filter((r) => status === 'all' || (status === 'placed' ? !!r.location : !r.location)),
    [base, status],
  );

  const save = async (r: SlotRow, body: any): Promise<boolean> => {
    setState((st) => ({ ...st, [r.groupId]: 'saving' }));
    const resp = await dispatch(actions.assignWarehouseSlot, body, token, { groupId: r.groupId });
    if (isErr(resp)) { setState((st) => ({ ...st, [r.groupId]: 'error' })); flash('err', `${r.name}: ${errMsg(resp)}`); return false; }
    r.location = resp.location ?? r.location;
    r.overflowLocations = resp.overflowLocations ?? r.overflowLocations;
    if (resp.resolvedSize) { r.sizeClass = resp.resolvedSize; r.sizeSource = resp.sizeSource; r.sizeGuessed = resp.sizeSource === 'guess'; }
    r.cartonCm = resp.cartonCm && (resp.cartonCm.l || resp.cartonCm.w || resp.cartonCm.h) ? resp.cartonCm : null;
    r.weightKg = resp.weightKg ?? null;
    if (body.packingNote !== undefined) r.packingNote = resp.packingNote || '';
    r.locationKnown = !r.location || codes.includes(r.location);
    r.locationUpdatedAt = body.location !== undefined ? new Date().toISOString() : r.locationUpdatedAt;
    setDraftSlot((d) => { const n = { ...d }; delete n[r.groupId]; return n; });
    setState((st) => ({ ...st, [r.groupId]: 'saved' }));
    setTimeout(() => setState((st) => (st[r.groupId] === 'saved' ? { ...st, [r.groupId]: undefined } : st)), 5000);
    flash('ok', `تحفظ — ${r.name}${body.location !== undefined ? ` ← البلاصة ${r.location || 'والو'}` : ''}${resp.resolvedSize ? ` · الحجم ${resp.resolvedSize}` : ''}`);
    return true;
  };

  const slotDirty = (row: SlotRow) => draftSlot[row.groupId] !== undefined && draftSlot[row.groupId] !== row.location;
  const saveSlot = (row: SlotRow) => { if (slotDirty(row)) save(row, { location: draftSlot[row.groupId] }); };

  const toggleAll = () => setSel(sel.size === view.length ? new Set() : new Set(view.map((r) => r.groupId)));
  const printSelected = async () => {
    if (!sel.size) return;
    setBusy(true);
    const r = await dispatch(actions.warehouseProductLabels, { groupIds: Array.from(sel) }, token);
    setBusy(false);
    if (!openBlob(r)) flash('err', 'ما قدرناش نوجدو الملصقات');
  };
  const bulkSize = async (sz: Size) => {
    if (!sel.size) return;
    if (!window.confirm(`نحطّو الحجم ${sz} على ${sel.size} منتج مختار؟`)) return;
    setBusy(true);
    const r = await dispatch(actions.assignWarehouseBulk, { items: Array.from(sel).map((groupId) => ({ groupId, sizeClass: sz })) }, token);
    setBusy(false);
    if (isErr(r)) return flash('err', errMsg(r));
    flash('ok', `الحجم ${sz} تحطّ على ${r.applied} منتج`);
    reload();
  };

  return (
    <div className="wh__card">
      <div className="wh__card-title"><Boxes size={16} /> المنتجات والبلايص</div>
      <div className="wh__card-sub">كليكي على <b>الحجم</b> ولا <b>✎</b> باش تبدّل منتج (الحجم، قياس الكرطونة، البلاصة، الاحتياط، ملاحظة) — حتى حاجة ما كتبدّل حتى تضغط <b>حفظ</b>. كود البلاصة تقدر تكتبو حتى فالجدول: ضغط Enter ولا حفظ ديال السطر. A = اللي كيتباعو بزاف (80% من الخرجات)، B = 15% اللي موراهم، C = 5% اللخرين، الحمر = حتى بيع ف 90 يوم.</div>
      <div className="wh__toolbar">
        <div style={{ position: 'relative' }}>
          <Search size={13} style={{ position: 'absolute', insetInlineStart: 8, top: 10, color: '#9CA3AF' }} />
          <input className="wh__input" style={{ paddingInlineStart: 26, width: 220 }} placeholder="قلّب بالسمية، SKU ولا البلاصة…" value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
        <select className="wh__select" value={status} onChange={(e) => setStatus(e.target.value as any)}>
          <option value="unplaced">غير اللي بلا بلاصة ({unplacedCount})</option><option value="placed">غير اللي عندها بلاصة ({placedCount})</option><option value="all">الكل ({base.length})</option>
        </select>
        <select className="wh__select" value={abc} onChange={(e) => setAbc(e.target.value as any)}>
          <option value="">كل الكلاسات</option><option value="A">A — سريع</option><option value="B">B</option><option value="C">C — بطيء</option><option value="-">بلا بيع ف 90 يوم</option>
        </select>
        <select className="wh__select" value={size} onChange={(e) => setSize(e.target.value as any)}>
          <option value="">كل الأحجام</option>{SIZES.map((x) => <option key={x} value={x}>{x} — {SIZE_HINT[x]}</option>)}
        </select>
        <label className="wh__check"><input type="checkbox" checked={onlyNeeded} onChange={(e) => setOnlyNeeded(e.target.checked)} /> غير المنتجات اللي فيها السلعة ولا تباعت مؤخرا</label>
        <span className="wh__muted" style={{ marginInlineStart: 'auto' }}>{view.length} منتج · <b style={{ color: '#1D9E75' }}>{placedCount} عندها بلاصة</b> · <b style={{ color: unplacedCount ? '#B45309' : '#1D9E75' }}>{unplacedCount} بلا بلاصة</b></span>
      </div>

      <div className="wh__table-wrap" style={{ maxHeight: '70vh' }}>
        <table className="wh__table wh__table--slots">
          <thead>
            <tr>
              <th><input type="checkbox" checked={view.length > 0 && sel.size === view.length} onChange={toggleAll} /></th>
              <th></th><th>المنتج</th><th>الكلاس</th><th className="num wh__hide-sm">خرجات 90 يوم / 30 يوم</th><th>المخزون</th>
              <th>الحجم</th><th>البلاصة</th><th className="wh__hide-sm">احتياط</th><th className="wh__hide-sm">اقتراح</th><th>الحالة</th><th></th>
            </tr>
          </thead>
          <tbody>
            {view.map((row) => {
              const draft = draftSlot[row.groupId] ?? row.location;
              const dirty = slotDirty(row);
              const st = state[row.groupId];
              return (
                <tr key={row.groupId} className={!row.location && row.needsSlot ? 'unplaced' : ''}>
                  <td><input type="checkbox" checked={sel.has(row.groupId)} onChange={() => setSel((x) => { const n = new Set(x); n.has(row.groupId) ? n.delete(row.groupId) : n.add(row.groupId); return n; })} /></td>
                  <td>{row.imageUrl ? <img className="wh__thumb" src={row.imageUrl} alt="" loading="lazy" /> : <div className="wh__thumb" />}</td>
                  <td>
                    <div className="wh__name" title={row.name}>{row.name}</div>
                    <div className="wh__muted">{row.skus[0]}{row.skuCount > 1 ? ` +${row.skuCount - 1} SKU` : ''}{row.packingNote ? ` · 📝 ${row.packingNote}` : ''}</div>
                  </td>
                  <td><span className={`wh__chip wh__abc-${row.abc}`}>{row.abc === '-' ? 'والو' : row.abc}</span></td>
                  <td className="num wh__hide-sm">{row.units90} / {row.units30}</td>
                  <td><StockLevel stock={row.stock} />{row.damaged ? <span className="wh__muted"> · فيه المخسور</span> : ''}</td>
                  <td>
                    <button type="button" className={`wh__sizebtn wh__sizebtn--${row.sizeSource}`} onClick={() => setEditing(row)} title={`${SIZE_HINT[row.sizeClass]} — ${row.sizeSource === 'set' ? 'حطّيتيه نتا' : row.sizeSource === 'dims' ? 'من قياس الكرطونة' : 'متخمّن من السمية، أكّدو عافاك'}. كليكي باش تبدّلو.`}>
                      <b>{row.sizeClass}</b>
                      <small>{row.sizeSource === 'set' ? '✓' : row.sizeSource === 'dims' ? (row.cartonCm ? `${row.cartonCm.l ?? ''}×${row.cartonCm.w ?? ''}×${row.cartonCm.h ?? ''}` : 'سم') : 'تخمين؟'}</small>
                    </button>
                  </td>
                  <td>
                    <div className="wh__slotcell">
                      <input
                        className={`wh__input wh__input--sm wh__input--code ${dirty ? 'dirty' : ''}`}
                        value={draft}
                        placeholder="أي كود، مثلا B12"
                        onChange={(e) => setDraftSlot((d) => ({ ...d, [row.groupId]: e.target.value.toUpperCase() }))}
                        onKeyDown={(e) => { if (e.key === 'Enter') saveSlot(row); if (e.key === 'Escape') setDraftSlot((d) => { const n = { ...d }; delete n[row.groupId]; return n; }); }}
                      />
                      {row.zone && !dirty && <span className={`wh__zone wh__zone-${row.zone}`}>{row.zone}</span>}
                    </div>
                  </td>
                  <td className="wh__hide-sm wh__code">{row.overflowLocations.join(', ') || <span className="wh__muted">—</span>}</td>
                  <td className="wh__hide-sm">{row.suggestedZone && <span className={`wh__zone wh__zone-${row.suggestedZone}`}>{row.suggestedZone}</span>}</td>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    {dirty ? (
                      <button className="wh__btn wh__btn--sm wh__btn--primary" onClick={() => saveSlot(row)} disabled={st === 'saving'}>حفظ</button>
                    ) : st === 'saving' ? <span className="wh__muted">كيتحفظ…</span>
                      : st === 'saved' ? <span className="wh__chip wh__abc-A">تحفظ ✓</span>
                      : st === 'error' ? <span className="wh__chip wh__abc--">ما دازش</span>
                      : <span className="wh__muted">{ago(row.locationUpdatedAt)}</span>}
                  </td>
                  <td><button className="wh__btn wh__btn--sm" title="بدّل الحجم، الكرطونة، البلاصة، الاحتياط، الملاحظة" onClick={() => setEditing(row)}>✎</button></td>
                </tr>
              );
            })}
            {view.length === 0 && <tr><td colSpan={12} className="wh__muted" style={{ textAlign: 'center', padding: '1.5rem' }}>حتى حاجة ما كتطابق — جرّب "الكل" فالفلتر ديال الحالة.</td></tr>}
          </tbody>
        </table>
      </div>

      {sel.size > 0 && (
        <div className="wh__bulkbar">
          <b>{sel.size} مختار</b>
          <button className="wh__btn wh__btn--sm" onClick={printSelected} disabled={busy}><Printer size={13} /> طبع ملصقات المنتجات (100×100)</button>
          <span style={{ opacity: 0.7 }}>حطّ الحجم:</span>
          {SIZES.map((x) => <button key={x} className="wh__btn wh__btn--sm" disabled={busy} onClick={() => bulkSize(x)}>{x}</button>)}
          <button className="wh__btn wh__btn--sm" style={{ marginInlineStart: 'auto' }} onClick={() => setSel(new Set())}>مسح الاختيار</button>
        </div>
      )}

      {editing && <ProductEditor row={editing} codes={codes} onClose={() => setEditing(null)} onSave={async (body) => { const ok = await save(editing, body); if (ok) setEditing(null); }} />}
    </div>
  );
};

// ─── Product editor (modal / bottom sheet) ───────────────────────────────────
const ProductEditor: FC<{ row: SlotRow; codes: string[]; onClose: () => void; onSave: (body: any) => Promise<void> }> = ({ row, codes, onClose, onSave }) => {
  const [sizeChoice, setSizeChoice] = useState<Size | 'auto'>(row.sizeSource === 'set' ? row.sizeClass : 'auto');
  const [l, setL] = useState(row.cartonCm?.l != null ? String(row.cartonCm.l) : '');
  const [w, setW] = useState(row.cartonCm?.w != null ? String(row.cartonCm.w) : '');
  const [h, setH] = useState(row.cartonCm?.h != null ? String(row.cartonCm.h) : '');
  const [kg, setKg] = useState(row.weightKg != null ? String(row.weightKg) : '');
  const [location, setLocation] = useState(row.location);
  const [overflow, setOverflow] = useState(row.overflowLocations.join(', '));
  const [note, setNote] = useState(row.packingNote);
  const [saving, setSaving] = useState(false);

  // same thresholds as the server, for the live preview only
  const autoSize = (() => {
    const d = [Number(l), Number(w), Number(h)].filter((x) => x > 0);
    if (d.length < 2) return null;
    const longest = Math.max(...d);
    const litres = d.length === 3 ? (d[0] * d[1] * d[2]) / 1000 : (d[0] * d[1] * 10) / 1000;
    const weight = Number(kg) || 0;
    if (weight > 25 || longest > 160 || litres > 300) return 'XXL' as Size;
    if (weight > 15 || longest > 60 || litres > 60) return 'XL' as Size;
    if (longest > 30 || litres > 10) return 'M' as Size;
    return 'S' as Size;
  })();
  const effective: Size = sizeChoice !== 'auto' ? sizeChoice : autoSize || row.sizeClass;

  const submit = async () => {
    setSaving(true);
    await onSave({
      sizeClass: sizeChoice === 'auto' ? null : sizeChoice,
      cartonCm: { l: l === '' ? null : Number(l), w: w === '' ? null : Number(w), h: h === '' ? null : Number(h) },
      weightKg: kg === '' ? null : Number(kg),
      location: location.toUpperCase().trim(),
      overflowLocations: overflow.split(/[,\s]+/).filter(Boolean),
      packingNote: note,
    });
    setSaving(false);
  };
  const changed = (sizeChoice === 'auto' ? null : sizeChoice) !== (row.sizeSource === 'set' ? row.sizeClass : null)
    || l !== (row.cartonCm?.l != null ? String(row.cartonCm.l) : '') || w !== (row.cartonCm?.w != null ? String(row.cartonCm.w) : '') || h !== (row.cartonCm?.h != null ? String(row.cartonCm.h) : '')
    || kg !== (row.weightKg != null ? String(row.weightKg) : '') || location.toUpperCase().trim() !== row.location || overflow !== row.overflowLocations.join(', ') || note !== row.packingNote;

  return (
    <div className="wh__overlay" onClick={() => !saving && onClose()}>
      <div className="wh__sheet" onClick={(e) => e.stopPropagation()} role="dialog" aria-label="تبديل المنتج">
        <div className="wh__sheet-head">
          {row.imageUrl && <img className="wh__thumb" style={{ width: 56, height: 56 }} src={row.imageUrl} alt="" />}
          <div style={{ minWidth: 0 }}>
            <div style={{ fontWeight: 700, lineHeight: 1.2 }}>{row.name}</div>
            <div className="wh__muted">{row.skus[0]}{row.skuCount > 1 ? ` +${row.skuCount - 1} SKU` : ''} · كلاس {row.abc === '-' ? 'والو' : row.abc} · <StockLevel stock={row.stock} /></div>
          </div>
          <button className="wh__btn wh__btn--sm" style={{ marginInlineStart: 'auto' }} onClick={onClose} disabled={saving}>✕</button>
        </div>

        <div className="wh__sheet-body">
          <div className="wh__field-label">الحجم <span className="wh__muted">— كيقرّر واش الرف (S/M/L) ولا الحيط (XL/XXL)؛ L عمرو ما كيتخمّن، غير نتا اللي كتختارو</span></div>
          <div className="wh__sizegrid">
            {SIZES.map((x) => (
              <button key={x} type="button" className={`wh__sizeopt ${sizeChoice === x ? 'wh__sizeopt--on' : ''}`} onClick={() => setSizeChoice(x)}>
                <b>{x}</b><small>{SIZE_HINT[x]}</small>
              </button>
            ))}
            <button type="button" className={`wh__sizeopt ${sizeChoice === 'auto' ? 'wh__sizeopt--on' : ''}`} onClick={() => setSizeChoice('auto')}>
              <b>أوتو</b><small>{autoSize ? `من قياس الكرطونة ← ${autoSize}` : `ما كاينش قياس الكرطونة ← تخمين من السمية (${row.sizeGuessed || row.sizeSource === 'guess' ? row.sizeClass : '…'})`}</small>
            </button>
          </div>

          <div className="wh__field-label">قياس الكرطونة بالسنتيمتر <span className="wh__muted">— اختياري، كيتستعمل ف أوتو وفالتغليف</span></div>
          <div className="wh__dims">
            <input className="wh__input" inputMode="decimal" placeholder="الطول" value={l} onChange={(e) => setL(e.target.value)} /><span>×</span>
            <input className="wh__input" inputMode="decimal" placeholder="العرض" value={w} onChange={(e) => setW(e.target.value)} /><span>×</span>
            <input className="wh__input" inputMode="decimal" placeholder="العلو" value={h} onChange={(e) => setH(e.target.value)} />
            <input className="wh__input" inputMode="decimal" placeholder="كيلو" value={kg} onChange={(e) => setKg(e.target.value)} style={{ marginInlineStart: 8 }} />
          </div>

          <div className="wh__field-label">كود البلاصة</div>
          <input className="wh__input wh__input--code" style={{ width: 140, height: 40, fontSize: '1.2rem' }} value={location} placeholder="أي كود، مثلا B12" onChange={(e) => setLocation(e.target.value.toUpperCase())} />

          <div className="wh__field-label">بلايص الاحتياط <span className="wh__muted">— السلعة الزايدة، فرّق بينهم بفاصلة</span></div>
          <input className="wh__input" style={{ width: '100%' }} value={overflow} dir="ltr" placeholder="F7, A08" onChange={(e) => setOverflow(e.target.value)} />

          <div className="wh__field-label">ملاحظة للي كيغلّف</div>
          <input className="wh__input" style={{ width: '100%' }} value={note} placeholder="مثلا 2 كرطونات فالقطعة، زاج كيتهرّس" onChange={(e) => setNote(e.target.value)} />
        </div>

        <div className="wh__sheet-foot">
          <div className="wh__muted">غادي يتحفظ بالحجم <b style={{ color: '#1A1D23' }}>{effective}</b>{sizeChoice === 'auto' ? ' (أوتو)' : ''}{location ? ` · البلاصة ${location.toUpperCase().trim()}` : ''}</div>
          <button className="wh__btn" onClick={onClose} disabled={saving}>إلغاء</button>
          <button className="wh__btn wh__btn--primary" onClick={submit} disabled={saving || !changed} style={{ minWidth: 110, height: 40 }}>{saving ? 'كيتحفظ…' : 'حفظ'}</button>
        </div>
      </div>
    </div>
  );
};

// ─── Map tab ─────────────────────────────────────────────────────────────────
const MapTab: FC<{ map: MapResp; token: string; flash: (k: 'ok' | 'err' | 'info', t: string) => void; reload: () => void }> = ({ map, token, flash, reload }) => {
  const [form, setForm] = useState({ aisle: 'A', zone: 'FAST' as Zone, bays: 4, levels: { 0: true, 1: true, 2: true } as Record<number, boolean>, capacity: 4, type: 'shelf', flat: false, numbering: 'sequential' as 'sequential' | 'level' });
  const preview = (() => {
    const lv = Object.entries(form.levels).filter(([, v]) => v).map(([k]) => Number(k)).sort((a, b) => a - b);
    if (form.flat) return `${form.aisle}1 … ${form.aisle}${form.bays}`;
    if (!lv.length) return 'ختار طبقة';
    if (form.numbering === 'sequential') {
      const shelves = lv.filter((x) => x > 0).length;
      const parts: string[] = [];
      if (shelves) parts.push(`الرفوف ${form.aisle}1 … ${form.aisle}${shelves * form.bays} (${form.aisle}1 = خانة 1 طبقة ${lv.filter((x) => x > 0)[0]}، خانة 2 كتبدا من ${form.aisle}${shelves + 1})`);
      if (lv.includes(0)) parts.push(`الأرض ${Array.from({ length: form.bays }, (_, i) => `${form.aisle}0${shelves ? i * shelves + 1 : i + 1}`).join(', ')}`);
      return parts.join('  ·  ');
    }
    return `${form.aisle}1-${lv[0]} … ${form.aisle}${form.bays}-${lv[lv.length - 1]}`;
  })();
  const [sel, setSel] = useState<Set<string>>(new Set());
  const [open, setOpen] = useState<Loc | null>(null);
  const [busy, setBusy] = useState(false);

  const grouped = useMemo(() => {
    const byZone = new Map<Zone, Map<string, Map<number, Loc[]>>>();
    for (const l of map.locations) {
      if (!byZone.has(l.zone)) byZone.set(l.zone, new Map());
      const z = byZone.get(l.zone)!;
      if (!z.has(l.aisle)) z.set(l.aisle, new Map());
      const a = z.get(l.aisle)!;
      if (!a.has(l.bay)) a.set(l.bay, []);
      a.get(l.bay)!.push(l);
    }
    return byZone;
  }, [map]);

  const generate = async () => {
    setBusy(true);
    const levels = Object.entries(form.levels).filter(([, v]) => v).map(([k]) => Number(k));
    const r = await dispatch(actions.generateWarehouseLocations, { aisle: form.aisle, zone: form.zone, bays: Number(form.bays), levels, capacity: Number(form.capacity), type: form.type, flat: form.flat, numbering: form.numbering }, token);
    setBusy(false);
    if (isErr(r)) return flash('err', errMsg(r));
    flash('ok', `تزادو ${r.created.length} بلاصة${r.skipped.length ? `، ${r.skipped.length} كانو ديجا` : ''}`);
    reload();
  };
  const [labelFormat, setLabelFormat] = useState<'auto' | 'beam' | 'full'>('auto');
  const [plainLabels, setPlainLabels] = useState(true);
  const printSel = async () => {
    const codes = sel.size ? Array.from(sel) : map.locations.map((l) => l.code);
    setBusy(true);
    const r = await dispatch(actions.warehouseLocationLabels, { codes, format: labelFormat === 'auto' ? undefined : labelFormat, plain: plainLabels }, token);
    setBusy(false);
    if (!openBlob(r)) flash('err', 'ما قدرناش نوجدو الملصقات');
  };
  const remove = async (l: Loc) => {
    if (l.used && !window.confirm(`${l.code} باقي فيها ${l.used} منتج. نمسحوها واخا هكا؟ أكواد البلايص ديال المنتجات غادي يبقاو مكتوبين.`)) return;
    const r = await dispatch(actions.deleteWarehouseLocation, {}, token, { id: l._id });
    if (isErr(r)) return flash('err', errMsg(r));
    setOpen(null);
    reload();
  };
  const update = async (l: Loc, patch: Partial<Loc>) => {
    const r = await dispatch(actions.updateWarehouseLocation, patch, token, { id: l._id });
    if (isErr(r)) return flash('err', errMsg(r));
    reload();
  };
  const toggleSel = (code: string) => setSel((s) => { const n = new Set(s); n.has(code) ? n.delete(code) : n.add(code); return n; });

  return (
    <>
      <div className="wh__card">
        <div className="wh__card-title"><Plus size={16} /> زيد البلايص ديال ممر واحد</div>
        <div className="wh__card-sub">كل كود = حرف الممر + رقم، كنحسبو من جيهة التغليف. الرفوف: <code>A1</code> = خانة 1 طبقة 1، <code>A2</code> = خانة 1 طبقة 2، <code>A3</code> = خانة 2 طبقة 1… الأرض تحت الخانة كتاخد الرقم ديال الرف اللي فوقها مع صفر قدّامو: تحت <span dir="ltr">A1/A2</span> هي <code>A01</code>، تحت <span dir="ltr">A3/A4</span> <code>A03</code>، تحت <span dir="ltr">A5/A6</span> <code>A05</code>… باش كود الأرض يتقرا حدا كود الرف. الحيط هو ممر مسطّح: <code>F1</code> … <code>F8</code>، رقم واحد لكل قسم.</div>
        <div className="wh__form">
          <label>حرف الممر<input className="wh__input" value={form.aisle} maxLength={2} onChange={(e) => setForm({ ...form, aisle: e.target.value.toUpperCase() })} /></label>
          <label>المنطقة<select className="wh__select" value={form.zone} onChange={(e) => { const z = e.target.value as Zone; setForm({ ...form, zone: z, flat: z === 'BULK' ? true : form.flat }); }}>{ZONES.map((z) => <option key={z} value={z}>{ZONE_LABEL[z]}</option>)}</select></label>
          <label>{form.flat ? 'الأقسام على طول الحيط' : 'الخانات (بين الأعمدة)'}<input className="wh__input" type="number" min={1} max={40} value={form.bays} onChange={(e) => setForm({ ...form, bays: Number(e.target.value) })} /></label>
          <label>حيط (غير الأرض)<div className="wh__levels"><label style={{ flexDirection: 'row', gap: 4 }}><input type="checkbox" checked={form.flat} onChange={(e) => setForm({ ...form, flat: e.target.checked })} /> حيط: <span dir="ltr">F1, F2…</span></label></div></label>
          {!form.flat && <label>الترقيم<select className="wh__select" value={form.numbering} onChange={(e) => setForm({ ...form, numbering: e.target.value as any })}><option value="sequential">رقم متتابع (A1, A2, A3…)</option><option value="level">خانة-طبقة (A1-0, A1-1, A1-2)</option></select></label>}
          {!form.flat && <label>الطبقات<div className="wh__levels" style={{ flexWrap: 'wrap', height: 'auto', minHeight: 32 }}>{[0, 1, 2, 3, 4, 5, 6].map((lv) => <label key={lv} style={{ flexDirection: 'row', gap: 3 }}><input type="checkbox" checked={!!form.levels[lv]} onChange={(e) => setForm({ ...form, levels: { ...form.levels, [lv]: e.target.checked } })} />{lv === 0 ? 'الأرض' : `L${lv}`}</label>)}</div></label>}
          <label>منتجات فالبلاصة<input className="wh__input" type="number" min={1} max={30} value={form.capacity} onChange={(e) => setForm({ ...form, capacity: Number(e.target.value) })} /></label>
          <button className="wh__btn wh__btn--primary" onClick={generate} disabled={busy || !form.aisle}><Plus size={14} /> زيد</button>
        </div>
        <div className="wh__muted" style={{ marginTop: '0.5rem' }}>غادي يتزادو: <span className="wh__code">{preview}</span></div>
      </div>

      <div className="wh__card">
        <div className="wh__toolbar">
          <div className="wh__card-title" style={{ marginBottom: 0 }}><MapPin size={16} /> البلايص ({map.locations.length})</div>
          <span className="wh__muted">كليكي على بلاصة باش تشوف شنو فيها · علّم باش تختار للطبع</span>
          <select className="wh__select" style={{ marginInlineStart: 'auto' }} value={labelFormat} onChange={(e) => setLabelFormat(e.target.value as any)} title="حديدة الرف ضيقة على الملصق ديال 100 ملم: شكل 'حديدة' كيحط الكود صغير شوية فالوسط باش الجناب المطويين يبقاو خاويين">
            <option value="auto">شكل الملصق: أوتو (الرف = حديدة، الحيط = كامل)</option>
            <option value="beam">حديدة — كود صغير فالوسط، للرفوف</option>
            <option value="full">كامل — كود كبير، للحيوط</option>
          </select>
          <label className="wh__check" title="الكود بوحدو، حروف كبار، بلا باركود"><input type="checkbox" checked={plainLabels} onChange={(e) => setPlainLabels(e.target.checked)} /> حروف غير (بلا باركود)</label>
          <button className="wh__btn" onClick={printSel} disabled={busy || !map.locations.length}><Printer size={14} /> طبع ملصقات {sel.size ? `${sel.size} بلاصة مختارة` : 'كل البلايص'}</button>
          {sel.size > 0 && <button className="wh__btn" onClick={() => setSel(new Set())}>مسح الاختيار</button>}
        </div>

        {map.orphans.length > 0 && (
          <div className="wh__msg wh__msg--err" style={{ marginBottom: '0.6rem' }}>
            <Info size={13} style={{ verticalAlign: -2 }} /> أكواد مستعملين ف منتجات ولكن ما مزاديينش كبلايص: <span dir="ltr">{map.orphans.map((o) => `${o.code} (${o.products.length})`).join(', ')}</span> — زيدهم لفوق ولا صلّح الغلطة.
          </div>
        )}

        {map.locations.length === 0 && <div className="wh__muted" style={{ padding: '1rem 0' }}>باقي ما كاينة حتى بلاصة. زيد أول ممر لفوق — مثلا الممر <b>A</b>، المنطقة FAST، 4 خانات، الأرض + طبقة 1 + طبقة 2 ← <span dir="ltr">A1…A8</span> و <span dir="ltr">A01, A03, A05, A07</span> — من بعد الحيط <b>F</b>، المنطقة BULK، 8 أقسام.</div>}

        {ZONES.filter((z) => grouped.has(z)).map((z) => {
          // places = capacity of every slot; a place is one product in a slot
          const statsOf = (ls: Loc[]) => ({ slots: ls.length, empty: ls.filter((l) => !l.used).length, left: ls.reduce((s, l) => s + Math.max(0, (l.capacity || 0) - (l.used || 0)), 0), places: ls.reduce((s, l) => s + (l.capacity || 0), 0) });
          const zoneLocs = Array.from(grouped.get(z)!.values()).flatMap((bays) => Array.from(bays.values()).flat());
          const zs = statsOf(zoneLocs);
          const badge = (s: ReturnType<typeof statsOf>) => (
            <span className="wh__muted" style={{ fontWeight: 500, marginInlineStart: 8 }} title="البلايص · البلايص الخاوية · المكان الباقي من المجموع">
              {s.slots} بلاصة · <b style={{ color: s.empty ? '#1D9E75' : '#6B7280' }}>{s.empty} خاوية</b> · <b style={{ color: s.left ? '#1D9E75' : '#E24B4A' }}>{s.left}</b>/{s.places} مكان باقي
            </span>
          );
          return (
          <div className="wh__zone-block" key={z}>
            <div className="wh__zone-head"><span className={`wh__zone wh__zone-${z}`}>{z}</span> {ZONE_LABEL[z]}{badge(zs)}</div>
            {Array.from(grouped.get(z)!.entries()).sort().map(([aisle, bays]) => (
              <div className="wh__aisle" key={aisle}>
                <div className="wh__aisle-head">{Array.from(bays.values())[0]?.[0]?.flat ? 'الحيط' : 'الممر'} {aisle} · {bays.size} {Array.from(bays.values())[0]?.[0]?.flat ? 'أقسام' : 'خانات'}{badge(statsOf(Array.from(bays.values()).flat()))}</div>
                <div className="wh__bays">
                  {Array.from(bays.entries()).sort((a, b) => a[0] - b[0]).map(([bay, locs]) => (
                    <div className="wh__bay" key={bay}>
                      {[...locs].sort((a, b) => b.level - a.level).map((l) => (
                        <div key={l.code} className={`wh__cell ${sel.has(l.code) ? 'wh__cell--sel' : ''} ${l.used >= l.capacity ? 'wh__cell--full' : l.used === 0 ? 'wh__cell--empty' : ''}`} onClick={() => setOpen(open?.code === l.code ? null : l)}>
                          <input type="checkbox" checked={sel.has(l.code)} onClick={(e) => e.stopPropagation()} onChange={() => toggleSel(l.code)} style={{ marginInlineEnd: 4 }} />
                          <span className="wh__cell-code">{l.code}</span>
                          <span className="wh__cell-used">{l.used}/{l.capacity}</span>
                          <div className="wh__cell-thumbs">{l.products.slice(0, 6).map((p, i) => p.imageUrl ? <img key={i} src={p.imageUrl} alt="" title={p.name} loading="lazy" /> : <span key={i} title={p.name}>▫</span>)}</div>
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
          );
        })}

        {open && (
          <div className="wh__drawer">
            <div className="wh__toolbar" style={{ marginBottom: '0.4rem' }}>
              <span className="wh__code wh__code--big">{open.code}</span>
              <span className={`wh__zone wh__zone-${open.zone}`}>{open.zone}</span>
              <span className="wh__muted">{open.type === 'shelf' ? 'رف' : open.type === 'floor' ? 'الأرض' : open.type} · كيدخل فيها <span dir="ltr">{open.fits.join('/')}</span> · {open.used}/{open.capacity} منتج</span>
              <select className="wh__select wh__input--sm" value={open.zone} onChange={(e) => update(open, { zone: e.target.value as Zone })}>{ZONES.map((z) => <option key={z} value={z}>{z} — {ZONE_LABEL[z]}</option>)}</select>
              <label className="wh__muted" style={{ display: 'flex', gap: 4, alignItems: 'center' }}>السعة <input className="wh__input wh__input--sm" type="number" style={{ width: 56 }} defaultValue={open.capacity} onBlur={(e) => Number(e.target.value) !== open.capacity && update(open, { capacity: Number(e.target.value) })} /></label>
              <label className="wh__muted" style={{ display: 'flex', gap: 4, alignItems: 'center' }}>كيدخل فيها XL <input type="checkbox" checked={open.fits.includes('XL')} onChange={(e) => update(open, { fits: e.target.checked ? Array.from(new Set([...open.fits, 'XL'])) as Size[] : open.fits.filter((f) => f !== 'XL') })} /></label>
              <button className="wh__btn wh__btn--sm" onClick={async () => { const r = await dispatch(actions.warehouseLocationLabels, { codes: [open.code], plain: plainLabels }, token); if (!openBlob(r)) flash('err', 'ما قدرناش نوجدو الملصق'); }}><Printer size={12} /> ملصق</button>
              <button className="wh__btn wh__btn--sm wh__btn--danger" onClick={() => remove(open)}><Trash2 size={12} /> مسح</button>
            </div>
            {open.products.length === 0 ? <div className="wh__muted">خاوية — حطّ المنتجات ف هاد الكود من تاب "المنتجات والبلايص".</div> : (
              <table className="wh__table">
                <tbody>
                  {open.products.map((p) => (
                    <tr key={p.groupId + p.kind}>
                      <td>{p.imageUrl && <img className="wh__thumb" src={p.imageUrl} alt="" />}</td>
                      <td><div className="wh__name">{p.name}</div><div className="wh__muted">{p.kind === 'overflow' ? 'سلعة احتياط' : 'البلاصة الأساسية'}</div></td>
                      <td><span className="wh__chip wh__abc-C">{p.sizeClass}</span></td>
                      <td><StockLevel stock={p.stock} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>
    </>
  );
};

// ─── Suggested moves tab ─────────────────────────────────────────────────────
const MovesTab: FC<{ moves: MovesResp | null; token: string; flash: (k: 'ok' | 'err' | 'info', t: string) => void; reload: () => void }> = ({ moves, token, flash, reload }) => {
  const [busy, setBusy] = useState<string | null>(null);
  const [showSnoozed, setShowSnoozed] = useState(false);
  const list = (moves?.suggestions || []).filter((m) => showSnoozed || !m.snoozedUntil);

  const apply = async (m: MoveItem, to: string) => {
    if (!to) return flash('err', 'ما كاينة حتى بلاصة خاوية ف هاد المنطقة — زيد بلايص ولا كبّر السعة الأول.');
    setBusy(m.groupId);
    const r = await dispatch(actions.assignWarehouseSlot, { location: to }, token, { groupId: m.groupId });
    setBusy(null);
    if (isErr(r)) return flash('err', errMsg(r));
    flash('ok', `${m.name} ← ${to}. حرّك الكرطونة ولصق ملصق جديد.`);
    reload();
  };
  const snooze = async (m: MoveItem, days: number) => {
    setBusy(m.groupId);
    const r = await dispatch(actions.snoozeWarehouseMove, { days }, token, { groupId: m.groupId });
    setBusy(null);
    if (isErr(r)) return flash('err', errMsg(r));
    reload();
  };

  return (
    <div className="wh__card">
      <div className="wh__card-title"><RefreshCw size={16} /> تبديلات مقترحة</div>
      <div className="wh__card-sub">
        كتتحسب من جديد مع كل تحديث من الخرجات ديال 30 و 90 يوم اللخرين. <b>لقدّام</b> = المنتج ولّا كيتباع كثر من المنطقة ديالو (كيتحرك ف شهر). <b>لور</b> = البيع ديالو نقص ف جوج المدات، يعني البلاصة FAST ديالو ضايعة (كيتحرك بشوية بالقصد). الكرطونات XL عمرهم ما كيتحركو؛ بلاصتهم فالحيط.
      </div>
      {moves && (
        <div className="wh__toolbar">
          <span className="wh__chip wh__abc-A">↑ {moves.counts.forward} لقدّام</span>
          <span className="wh__chip wh__abc-C">↓ {moves.counts.back} لور</span>
          {moves.counts.noSlot > 0 && <span className="wh__chip wh__abc--">{moves.counts.noSlot} بلا بلاصة خاوية</span>}
          <label style={{ fontSize: '0.74rem', marginInlineStart: 'auto', display: 'flex', gap: 4, alignItems: 'center' }}><input type="checkbox" checked={showSnoozed} onChange={(e) => setShowSnoozed(e.target.checked)} /> بيّن اللي مأجّلين ({moves.counts.snoozed})</label>
        </div>
      )}
      {list.length === 0 ? (
        <div className="wh__muted" style={{ padding: '1rem 0' }}>حتى حاجة ما خاصها تتحرك. كل منتج عندو بلاصة راه فالمنطقة اللي كتناسب البيع ديالو. شوف مرة أخرى السيمانة الجاية.</div>
      ) : (
        <div className="wh__table-wrap" style={{ maxHeight: '65vh' }}>
          <table className="wh__table">
            <thead><tr><th></th><th>المنتج</th><th>علاش</th><th className="num">30 يوم / 90 يوم</th><th>دابا</th><th>خاصو يكون ف</th><th>البلاصة الجديدة</th><th></th></tr></thead>
            <tbody>
              {list.map((m) => (
                <tr key={m.groupId} style={m.snoozedUntil ? { opacity: 0.5 } : undefined}>
                  <td>{m.imageUrl && <img className="wh__thumb" src={m.imageUrl} alt="" loading="lazy" />}</td>
                  <td><div className="wh__name">{m.name}</div><div className="wh__muted">{m.sizeClass} · <StockLevel stock={m.stock} /> · كلاس {m.abc90 === '-' ? 'والو' : m.abc90} (90 يوم) / {m.abc30 === '-' ? 'والو' : m.abc30} (30 يوم)</div></td>
                  <td style={{ maxWidth: 260 }}><span className={`wh__chip ${m.direction === 'forward' ? 'wh__abc-A' : 'wh__abc-C'}`}>{m.direction === 'forward' ? '↑ لقدّام' : '↓ لور'}</span><div className="wh__muted" style={{ marginTop: 2 }}>{m.reason}</div></td>
                  <td className="num">{m.units30} / {m.units90}</td>
                  <td><span className="wh__code">{m.from}</span> <span className={`wh__zone wh__zone-${m.fromZone}`}>{m.fromZone}</span></td>
                  <td><span className={`wh__zone wh__zone-${m.toZone}`}>{m.toZone}</span></td>
                  <td>
                    <input className="wh__input wh__input--sm wh__input--code" defaultValue={m.to || ''} placeholder="ما كاينة بلاصة خاوية" id={`mv-${m.groupId}`} />
                  </td>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    <button className="wh__btn wh__btn--sm wh__btn--primary" disabled={busy === m.groupId} onClick={() => apply(m, ((document.getElementById(`mv-${m.groupId}`) as HTMLInputElement)?.value || '').toUpperCase().trim())}>تحرّك ✓</button>{' '}
                    {m.snoozedUntil
                      ? <button className="wh__btn wh__btn--sm" disabled={busy === m.groupId} onClick={() => snooze(m, 0)}>رجّعها</button>
                      : <button className="wh__btn wh__btn--sm" disabled={busy === m.groupId} onClick={() => snooze(m, 30)} title="خبّيها 30 يوم">من بعد</button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// ─── Plan tab ────────────────────────────────────────────────────────────────
const PlanTab: FC<{ token: string; flash: (k: 'ok' | 'err' | 'info', t: string) => void; reload: () => void; hasLocations: boolean }> = ({ token, flash, reload, hasLocations }) => {
  const [plan, setPlan] = useState<PlanResp | null>(null);
  const [replan, setReplan] = useState(false);
  const [busy, setBusy] = useState(false);

  const build = async () => {
    setBusy(true);
    const r = await dispatch(actions.buildWarehousePlan, { replan }, token);
    setBusy(false);
    if (isErr(r)) return flash('err', errMsg(r));
    setPlan(r);
  };
  const apply = async () => {
    if (!plan?.plan.length) return;
    if (!window.confirm(`نكتبو ${plan.plan.length} كود ديال البلايص فالمخزون؟ دير هادشي من بعد ما تكون الكرطونات تحركو بالصح (ولا طبع الخطة الأول وحرّكهم).`)) return;
    setBusy(true);
    const r = await dispatch(actions.assignWarehouseBulk, { items: plan.plan.map((p) => ({ groupId: p.groupId, location: p.to })) }, token);
    setBusy(false);
    if (isErr(r)) return flash('err', errMsg(r));
    flash('ok', `${r.applied} منتج تحطّو فبلايصهم${r.failed ? `، ${r.failed} ما دازوش` : ''}`);
    setPlan(null);
    reload();
  };
  const pdf = async () => {
    if (!plan) return;
    setBusy(true);
    const r = await dispatch(actions.warehousePlanPdf, { plan: plan.plan }, token);
    setBusy(false);
    if (!openBlob(r)) flash('err', 'ما قدرناش نوجدو PDF ديال الخطة');
  };

  return (
    <div className="wh__card">
      <div className="wh__card-title"><Move size={16} /> خطة أوتوماتيكية</div>
      <div className="wh__card-sub">كتعمّر البلايص ديالك بوحدها: اللي كيتباعو بزاف الأولين ف FAST، الكرطونات XL ف بلايص الأرض BULK، والبطيئين فالجيهة البعيدة. حتى حاجة ما كتكتب حتى تضغط طبّق.</div>
      {!hasLocations && <div className="wh__msg" style={{ marginBottom: '0.6rem' }}>زيد البلايص ف تاب "خريطة البلايص" الأول (على الأقل ممر FAST واحد وبلاصة أرض BULK وحدة).</div>}
      <div className="wh__toolbar">
        <label style={{ fontSize: '0.76rem', display: 'flex', gap: 4, alignItems: 'center' }}><input type="checkbox" checked={replan} onChange={(e) => setReplan(e.target.checked)} /> عاود الخطة كاملة (بلا ما نحسبو البلايص الحالية)</label>
        <button className="wh__btn wh__btn--primary" onClick={build} disabled={busy || !hasLocations}>وجّد الخطة</button>
        {plan && plan.plan.length > 0 && <>
          <button className="wh__btn" onClick={pdf} disabled={busy}><Printer size={14} /> طبع خطة التبديل (A4)</button>
          <button className="wh__btn" onClick={async () => { setBusy(true); const r = await dispatch(actions.warehouseProductLabels, { groupIds: plan.plan.map((p) => p.groupId) }, token); setBusy(false); if (!openBlob(r)) flash('err', 'ما قدرناش نوجدو الملصقات'); }} disabled={busy}><Printer size={14} /> طبع ملصقات المنتجات</button>
          <button className="wh__btn wh__btn--primary" onClick={apply} disabled={busy}>طبّق {plan.plan.length} تبديل</button>
        </>}
      </div>
      {plan && (
        <>
          <div className="wh__muted" style={{ marginBottom: '0.5rem' }}>
            {plan.plan.length} اقتراح · {plan.unplaced.length} ما لقيناش ليهم بلاصة (حتى بلاصة خاوية كتناسب) · {plan.capacityLeft} بلاصة باقي فيها المكان
          </div>
          {plan.unplaced.length > 0 && (
            <div className="wh__msg wh__msg--err" style={{ marginBottom: '0.6rem' }}>
              ما كاينش المكان لـ: {plan.unplaced.slice(0, 12).map((u) => `${u.name} (${u.sizeClass}${u.suggestedZone ? ' → ' + u.suggestedZone : ''})`).join(' · ')}{plan.unplaced.length > 12 ? ` … +${plan.unplaced.length - 12}` : ''}. زيد بلايص لهاد المنطقة ولا كبّر السعة.
            </div>
          )}
          <div className="wh__table-wrap" style={{ maxHeight: '60vh' }}>
            <table className="wh__table">
              <thead><tr><th></th><th>المنتج</th><th>الكلاس</th><th>الحجم</th><th className="num">خرجات 90 يوم</th><th>المخزون</th><th>من</th><th>لـ</th><th>المنطقة</th></tr></thead>
              <tbody>
                {plan.plan.map((p) => (
                  <tr key={p.groupId}>
                    <td>{p.imageUrl && <img className="wh__thumb" src={p.imageUrl} alt="" loading="lazy" />}</td>
                    <td><div className="wh__name">{p.name}</div></td>
                    <td><span className={`wh__chip wh__abc-${p.abc}`}>{p.abc === '-' ? 'والو' : p.abc}</span></td>
                    <td>{p.sizeClass}</td>
                    <td className="num">{p.units90}</td>
                    <td><StockLevel stock={p.stock} /></td>
                    <td className="wh__code">{p.from || <span className="wh__muted">—</span>}</td>
                    <td className="wh__code wh__code--big">{p.to}</td>
                    <td><span className={`wh__zone wh__zone-${p.zone}`}>{p.zone}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
};

// ─── Guide tab ───────────────────────────────────────────────────────────────
const GuideTab: FC = () => (
  <div className="wh__guide">
    <div className="wh__card">
      <h3><MapPin size={15} /> 1. كود واحد لكل منتج، مطبوع جوج مرات</h3>
      <ul>
        <li>الأكواد هي حرف + رقم، كنحسبو من جيهة التغليف: الرفوف <code>A1</code>، <code>A2</code>، <code>A3</code>… (الرف اللي لتحت الأول، من بعد الخانة اللي موراها)، الأرض تحت الخانة <code>A01</code>، <code>A03</code>، <code>A05</code>… (صفر + الرف اللي فوقها)، أقسام الحيط <code>F1</code> … <code>F8</code>.</li>
        <li>كل منتج كياخد بلاصة أساسية <b>وحدة</b>، مثلا <code>A5</code>. الكرطونات الزايدين كيمشيو لبلاصة احتياط (الحيط / الطبقة اللفوقانية) مكتوبة فالتطبيق.</li>
        <li>ملصق البلاصة على العمود ديال الرف (كود كبير + باركود)، ملصق المنتج على الكرطونة / الصندوق (تصويرة + كود + SKU).</li>
        <li>القاعدة للي كيغلّف: <b>شوف التطبيق ← قرا الكود ← سير لتمّا</b>. عمرك ما تقلّب بالذاكرة.</li>
        <li>القاعدة للي كيحرّك شي كرطونة: <b>بدّل الكود فالتطبيق ف نفس الدقيقة</b>. كرطونة بلا بلاصة = "ضايعة".</li>
      </ul>
    </div>
    <div className="wh__card">
      <h3><Layers size={15} /> 2. المناطق — البعد = السرعة</h3>
      <ul>
        <li><span className="wh__zone wh__zone-FAST">FAST</span> الممر اللي لاصق مع طبلة التغليف: كلاس A (تقريبا 80 منتج كيديرو 80% من الخرجات). طبقة العين (طبقة 1) هي الأولى.</li>
        <li><span className="wh__zone wh__zone-BULK">BULK</span> بلايص الأرض على طول الحيط للكرطونات XL: علّاقات الحوايج (3ALA9A)، الطبالي، النشّافات، الكليماتيزورات. حطّهم <b>واقفين</b> مع الحيط والملصق قبالة الممر، منتج واحد فكل بلاصة أرض.</li>
        <li><span className="wh__zone wh__zone-MEDIUM">MEDIUM</span> الممرات اللي موراه لكلاس B. <span className="wh__zone wh__zone-SLOW">SLOW</span> الجيهة البعيدة لكلاس C ولكثر من 300 منتج اللي ما تباعو حتى مرة ف 90 يوم.</li>
        <li><span className="wh__zone wh__zone-RETURNS">RETURNS</span> بلاصة وحدة معلّمة للسلعة المرجوعة / المخسورة. حتى حاجة ما كترجع للرف قبل ما تتشاف وتتحسب.</li>
        <li><span className="wh__zone wh__zone-PACKING">PACKING</span> الطبلة + الطابعة + البلاستيك بالبولات + الكرطونات المطويين. خلّي الربطات ديال الكرطونات المطويين <b>برّا</b> من الممرات (التصويرة كتبيّن بلي كيسدّو منطقة FAST).</li>
      </ul>
    </div>
    <div className="wh__card">
      <h3><Package size={15} /> 3. الحوايج الصغار: صناديق، ماشي كرطونات محلولين</h3>
      <ul>
        <li>الأحجام حسب الكرطونة: <b>S</b> حتى 30 سم و 10 لتر (صندوق فوق الرف) · <b>M</b> حتى 60 سم و 60 لتر (كرطونة فوق الرف) · <b>L</b> كرطونة كبيرة ولكن كتدخل فطبقة ديال الرف — كتختارها باليد غير، عمرها ما كتتخمّن · <b>XL</b> أي حاجة كبر، حتى 160 سم و 25 كيلو (الحيط) · <b>XXL</b> أطول ولا أتقل، جوج د الناس (الحيط، الأقسام القراب). كتب قياس الكرطونة ف تاب "المنتجات والبلايص" و S / M / XL / XXL كيتحسب بوحدو.</li>
        <li>منتجات S (القلادات، السمطة، الكابلات، الگادجيت) كيمشيو ف صناديق بلاستيك ولا كرطونات مقطوعين، منتج <b>واحد</b> فكل صندوق، والملصق لقدّام.</li>
        <li>الكرطونات ديال المورّد المحلولين وفيهم حوايج مخلّطين فالأرض هوما السبب رقم 1 باش المنتجات "ما كتتلقاش". خوّيهم فالصناديق نهار اللي كيوصلو.</li>
        <li>الكرطونات المقطّعين فالرف اللفوقاني: بدّلهم بكرطونة جديدة ولا حطّهم ف صندوق. كرطونة مقطّعة = خطر الضياع وخطر التهراس.</li>
      </ul>
    </div>
    <div className="wh__card">
      <h3><Move size={15} /> 4. التطبيق ف 4 نص نهارات</h3>
      <ol>
        <li>زيد البلايص هنا (تاب "خريطة البلايص"): ممر بممر، حسب الخانات فالرف، 3 طبقات. طبع ولصق ملصقات البلايص.</li>
        <li>أكّد الأحجام: ف تاب "المنتجات والبلايص" شوف الأحجام المكتوب عليها "تخمين"، وصلّح اللي غالطين (كتاخد 20 دقيقة).</li>
        <li>الخطة الأوتوماتيكية ← طبع خطة التبديل A4 ← حرّك الكرطونات وعلّم على كل سطر. ملي تسالي طبّق الخطة فالتطبيق.</li>
        <li>طبع ملصقات المنتجات اللي تحرّكو، ولصقهم لقدّام فكل كرطونة / صندوق. حلّ تطبيق السكانير: دابا كل خرجة كتبيّن كود البلاصة.</li>
      </ol>
      <div className="wh__muted" style={{ marginTop: 6 }}>كل نهار: السلعة الجديدة كتاخد بلاصة قبل ما تخرج من بلاصة الاستقبال. كل سيمانة: فلتر "بلا بلاصة" خاصو يكون صفر؛ وحسب ممر واحد.</div>
    </div>
  </div>
);

export default WarehousePage;
